import { Controller } from "@hotwired/stimulus";

export default class EmojiMartDropdownController extends Controller {
  static targets = ["toggle", "menu", "picker"];

  connect() {
    this.isOpening = false;
    this.watchFrame = null;
    this.showRequest = 0;

    this.handleOutsidePointerDown = this.handleOutsidePointerDown.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
    this.handleOuterScroll = this.handleOuterScroll.bind(this);
    this.watchDistance = this.watchDistance.bind(this);
    this.hide = this.hide.bind(this);
    document.addEventListener("pointerdown", this.handleOutsidePointerDown, true);
    document.addEventListener("keydown", this.handleEscape);
    globalThis.addEventListener("scroll", this.handleOuterScroll, { capture: true, passive: true });
    this.element.addEventListener("emoji-mart-dropdown:hide", this.hide);
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.handleOutsidePointerDown, true);
    document.removeEventListener("keydown", this.handleEscape);
    globalThis.removeEventListener("scroll", this.handleOuterScroll, true);
    this.element.removeEventListener("emoji-mart-dropdown:hide", this.hide);

    this.menuTarget.hidePopover?.();
    this.emojiMartController?.stopVisibleEmojiPreloading?.();
    this.stopWatchingDistance();
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen()) {
      this.hide();
    } else {
      this.show();
    }
  }

  async show() {
    if (!this.emojiMartController) {
      await this.preparePicker();
    }

    if (this.checkLength()) {
      alert("You can add a maximum of 20 reactions to the same message.");
      return;
    }

    const request = ++this.showRequest;
    this.isOpening = true;
    this.menuTarget.classList.remove("d-none");
    this.menuTarget.style.visibility = "hidden";
    this.menuTarget.style.position = "fixed";
    this.menuTarget.style.left = "0px";
    this.menuTarget.style.top = "0px";
    this.menuTarget.style.zIndex = "9999";
    this.menuTarget.showPopover?.();
    this.toggleTarget.setAttribute("aria-busy", "true");

    await this.waitForPickerContent();
    if (request !== this.showRequest || !this.isOpen()) return;
    this.emojiMartController?.startVisibleEmojiPreloading?.();

    requestAnimationFrame(() => {
      if (request !== this.showRequest || !this.isOpen()) return;
      this.positionMenu();

      requestAnimationFrame(() => {
        if (request !== this.showRequest || !this.isOpen()) return;
        this.positionMenu();
        this.menuTarget.style.visibility = "visible";
        this.toggleTarget.removeAttribute("aria-busy");
        this.startWatchingDistance();
        this.isOpening = false;
      });
    });
  }

  hide(event = null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const picker = this.pickerTarget.querySelector("em-emoji-picker");
    const searchInput = picker?.shadowRoot?.querySelector(
      'input[type="search"]'
    );

    if (searchInput) {
      searchInput.value = "";
      searchInput.dispatchEvent(
        new Event("input", { bubbles: true, composed: true })
      );
    }

    if (!this.isOpen()) return;

    this.showRequest += 1;
    this.menuTarget.hidePopover?.();
    this.menuTarget.classList.add("d-none");
    this.toggleTarget.removeAttribute("aria-busy");
    this.isOpening = false;
    this.clearInlinePosition();
    this.emojiMartController?.stopVisibleEmojiPreloading?.();
    this.stopWatchingDistance();
  }

  async waitForPickerContent() {
    const timeoutAt = performance.now() + 10000;

    while (this.isOpen() && performance.now() < timeoutAt) {
      const picker = this.pickerTarget.querySelector("em-emoji-picker");
      const emojiButton = picker?.shadowRoot?.querySelector("button[aria-label]");
      if (emojiButton) return;

      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
  }

  async preparePicker() {
    const owner = this.element.closest("[data-controller~=\"emoji-mart\"]");
    if (!owner) return;

    let controller = this.application.getControllerForElementAndIdentifier(
      owner,
      "emoji-mart"
    );

    if (!controller) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      controller = this.application.getControllerForElementAndIdentifier(
        owner,
        "emoji-mart"
      );
    }

    await controller?.ensurePicker?.();
    this.emojiMartController = controller;
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  isOpen() {
    return !this.menuTarget.classList.contains("d-none");
  }

  handleOutsidePointerDown(event) {
    if (!this.isOpen()) return;
    if (this.isOpening) return;

    const path = event.composedPath ? event.composedPath() : [];
    const clickedInsideMenu = path.includes(this.menuTarget);
    const clickedToggle = path.includes(this.toggleTarget);

    if (clickedInsideMenu || clickedToggle) return;

    this.hide();
  }

  handleEscape(event) {
    if (event.key === "Escape") this.hide();
  }

  handleOuterScroll(event) {
    if (!this.isOpen()) return;
    const path = event.composedPath?.() || [];
    if (path.includes(this.menuTarget) || this.menuTarget.contains(event.target)) return;
    this.hide();
  }

  positionMenu() {
    const toggleRect = this.toggleTarget.getBoundingClientRect();
    const menu = this.menuTarget;
    const gap = 8;
    const viewportPadding = 8;
    const viewport = globalThis.visualViewport;
    const viewportLeft = viewport?.offsetLeft || 0;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportWidth = viewport?.width || globalThis.innerWidth;
    const viewportHeight = viewport?.height || globalThis.innerHeight;
    const minLeft = viewportLeft + viewportPadding;
    const minTop = viewportTop + viewportPadding;
    const maxRight = viewportLeft + viewportWidth - viewportPadding;
    const maxBottom = viewportTop + viewportHeight - viewportPadding;
    menu.style.setProperty("--emoji-menu-height", `${Math.max(0, viewportHeight - 2 * viewportPadding)}px`);

    menu.style.position = "fixed";
    menu.style.left = "0px";
    menu.style.top = "0px";
    menu.style.zIndex = "9999";

    const menuRect = menu.getBoundingClientRect();

    let left = toggleRect.left;
    let top = toggleRect.top - menuRect.height - gap;

    if (top < minTop) {
      top = toggleRect.bottom + gap;
    }

    if (left + menuRect.width > maxRight) {
      left = maxRight - menuRect.width;
    }

    if (left < minLeft) left = minLeft;

    if (top + menuRect.height > maxBottom) {
      top = Math.max(minTop, maxBottom - menuRect.height);
    }

    // A filtered ancestor can offset fixed coordinates in browsers without
    // popover support. Subtract the measured origin in that fallback.
    menu.style.left = `${Math.round(left - menuRect.left)}px`;
    menu.style.top = `${Math.round(top - menuRect.top)}px`;
  }

  startWatchingDistance() {
    this.stopWatchingDistance();
    this.watchFrame = requestAnimationFrame(this.watchDistance);
  }

  stopWatchingDistance() {
    if (this.watchFrame) {
      cancelAnimationFrame(this.watchFrame);
      this.watchFrame = null;
    }
  }

  watchDistance() {
    if (!this.isOpen()) return;
    this.positionMenu();

    const toggleRect = this.toggleTarget.getBoundingClientRect();
    const menuRect = this.menuTarget.getBoundingClientRect();

    const toggleOffScreen =
      toggleRect.bottom < 0 ||
      toggleRect.top > globalThis.innerHeight ||
      toggleRect.right < 0 ||
      toggleRect.left > globalThis.innerWidth;

    const horizontalBuffer = 180;
    const verticalBuffer = 500;

    const horizontallyDetached =
      menuRect.right < toggleRect.left - horizontalBuffer ||
      menuRect.left > toggleRect.right + horizontalBuffer;

    const verticallyDetached =
      menuRect.bottom < toggleRect.top - verticalBuffer ||
      menuRect.top > toggleRect.bottom + verticalBuffer;

    if (toggleOffScreen || horizontallyDetached || verticallyDetached) {
      this.hide();
      return;
    }

    this.watchFrame = requestAnimationFrame(this.watchDistance);
  }

  clearInlinePosition() {
    const menu = this.menuTarget;
    menu.style.removeProperty("--emoji-menu-height");
    menu.style.visibility = "";
    menu.style.left = "";
    menu.style.top = "";
    menu.style.position = "";
    menu.style.zIndex = "";
  }

  checkLength() {
    return this.element.parentElement.classList.contains("message-emoji") && this.emojiMartController.emojiArray.length >= 20;
  }
}
